import {Team, useApiCall, useApiEndpoint} from "@jane/lib/src/api";
import React, {ReactNode, useMemo} from "react"
import {JaneEmployee, useEmployeesData} from "../../resources/EmployeeContext";
import {useTeamData} from "../../resources/TeamContext";
import {usePermissions} from "../../resources/PermissionsHook";
import {config} from "../../config";
import { ContentContainer } from "@jane/lib/src/components/content/ContentContainer";
import { Loading } from "@jane/lib/src/components/Loading";
import {getRelatedTeamIds, useTeamMap} from "../../components/team-select";
import DynamicDataTable from "../../../../lib/src/components/data-table/DynamicDataTable";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckCircle, faTimesCircle, faWarning} from "@fortawesome/free-solid-svg-icons";
import { HoverHint } from "@jane/lib/src/components/content/HoverHint";
import {NavLink} from "react-router-dom";
import { PageHeader } from "@jane/lib/src/components/content/PageHeader";
import moment, {now} from "moment";

interface EmployeeWithTeamAndFullName extends JaneEmployee {
  team_name: string
  full_name: string
}
export default function EmployeesListPage(): ReactNode {
  const { employees, isLoading, error } = useEmployeesData()
  const { teams, isLoading: loadingTeams } = useTeamData()

  const { teams: teamsApi } = useApiCall(config)
  const { canListTeams } = usePermissions()
  const { resource: teamResource, isLoading: loadingTeam } = useApiEndpoint(() => teamsApi.myTeam(), canListTeams)
  const { resource: secondaryTeams, isLoading: loadingSecondaryTeams } = useApiEndpoint(() => teamsApi.myTeams(), canListTeams)

  return <ContentContainer size={'lg'}>
    <PageHeader>Medewerkers</PageHeader>

    <Loading loading={isLoading || loadingTeam || loadingTeams || loadingSecondaryTeams}>
      { !! employees && <FilteredEmployeeTable employees={employees} teams={teams} primaryTeam={teamResource!} secondaryTeams={secondaryTeams || []} />  }
    </Loading>
  </ContentContainer>
}

function FilteredEmployeeTable(props: {employees: JaneEmployee[], teams: Team[], primaryTeam: Team|null, secondaryTeams: Team[]}): ReactNode {
  const teamMap: {[id: number]: Team} = useMemo(() => {
    let teamMap: {[id: number]: Team} = {}
    props.teams.forEach(team => teamMap[team.id] = team)
    return teamMap
  }, [props.teams])
  const enrichedEmployees = useMemo(() => props.employees
    .map((employee: JaneEmployee): EmployeeWithTeamAndFullName => {
      return {
        ...employee,
        team_name: employee.team_id !== null && teamMap[employee.team_id] ? teamMap[employee.team_id].name : '-',
        full_name: `${employee.given_name} ${employee.family_name}`,
      }
    }), [props.employees, teamMap])

  return <div>
    <EmployeeTable employees={enrichedEmployees} teams={props.teams} primaryTeam={props.primaryTeam} secondaryTeams={props.secondaryTeams} />
  </div>
}

function EmployeeTable({employees, teams, primaryTeam, secondaryTeams}: {employees: EmployeeWithTeamAndFullName[], teams: Team[], primaryTeam: Team|null, secondaryTeams: Team[]}): ReactNode {
  const teamMap = useTeamMap(teams, primaryTeam, secondaryTeams)
  const {canListAllTeams,canLimitedListAllTeams} = usePermissions()
  const teamOptionsMap: Map<number, [string, number]> = useMemo(() => {
    return new Map([
      [-2, ['Alle', 0]],
      [-1, ['Mijn teams', 0]],
      ...teamMap.filter(teams => teams[0] !== undefined)
        .map<[Team, number]>(teams => [teams.slice(-1)[0], teams.length])
        .map<[number, [string, number]]>(([team, indentation]) => [team.id, [team.name, indentation]])
    ])
  }, [teamMap])
  const teamFilterOptions: [string,string][] = useMemo(() => {
    return Array.from(teamOptionsMap)
      .filter(([id]: [number, [string, number]]): boolean => {
        return !(canListAllTeams && id === -1)
      })
      .map(([id, [name, indentation]]: [number, [string, number]]): [string, string] => {
        return [id.toString(), (canListAllTeams ? "\xA0\xA0".repeat(indentation) : "") + name]
      })
  }, [teamOptionsMap, canListAllTeams])
  /** Used to look up related teams */
  const relatedTeamsMap: {[teamId: string]: string[]} = useMemo(() => {
    return Object.fromEntries(teams.map((team) => {
      return [team.id.toString(), getRelatedTeamIds(team, teamMap).map(id => id.toString())]
    }))
  }, [teamMap, teams])
  return <DynamicDataTable<EmployeeWithTeamAndFullName>
    placeholder={'Geen medewerkers'}
    searchableProperties={['given_name', 'family_name', 'full_name', 'email', 'team_name', 'employee_number']}
    keyProperty={'id'}
    compact
    data={employees}
    availableFilters={[
      {
        title: 'Team',
        property: 'team_id',
        colSpan: 2,
        options: teamFilterOptions,
        defaultValue: canListAllTeams ? '-2' : '-1',
        type: 'autocomplete',
        filterCallback: (row, filter) => {
          return filter === '-2'
            || (
              filter === '-1'
              && row.team_id !== null
              && teamFilterOptions.map(t => t[0]).includes(row.team_id.toString())
            )
            || (
              row.team_id !== null
              && relatedTeamsMap[filter]?.includes(row.team_id.toString())
            )
        },
      },
      {
        title: 'Sync status',
        property: 'sub',
        options: {
          'all': 'Alle',
          'synced': 'Jane account',
          'sync_error': 'Niet in systeem',
        },
        defaultValue: 'all',
        filterCallback: (row, filter) => {
          if (filter === 'synced') {
            return !!row.sub
          } else if (filter === 'sync_error') {
            return !row.sub
          }
          return true
        }
      },
      {
        title: 'Rol',
        property: 'role',
        options: {
          'all': 'Alle',
          'Werknemer': 'Werknemer',
          'Coordinator': 'Coordinator',
          'Manager': 'Manager',
        },
        defaultValue: 'all',
        filterCallback: (row, filter) => filter === 'all' ? true : row.role === filter,
      },
      {
        title: 'Email',
        property: 'email',
        options: {
          'all': 'Alle',
          'miep': '@miep.nu',
          'other': 'Anders',
          'none': 'Onbekend',
        },
        defaultValue: 'all',
        filterCallback: (row, filter) => {
          switch(filter) {
            case "miep": return row.email?.endsWith("@miep.nu")
            case "other": return row.email !== null && ! row.email.endsWith("@miep.nu")
            case "none": return row.email === null
            default: return true
          }
        },
      },
      {
        title: 'Startdatum',
        property: 'start_date',
        options: {
          'all': 'Alle',
          'recent': '+/- 3 weken t.o.v. nu',
          'known': 'Bekend',
          'none': 'Onbekend',
        },
        defaultValue: 'all',
        filterCallback: (row, filter) => {
          switch(filter) {
            case "known": return row.start_date !== null && row.start_date !== undefined
            case "none": return row.start_date === null || row.start_date === undefined
            case "recent":
              if (row.start_date === null || row.start_date === undefined) {
                return false
              }
              const date = moment(row.start_date)
              return date.isSameOrAfter(moment().subtract(3, 'weeks'), 'day') && date.isSameOrBefore(moment().add(3, 'weeks'), 'day')
            default: return true
          }
        },
      },
      {
        title: 'Profielfoto',
        property: 'employee_picture_url',
        options: {
          'all': 'Alle',
          'yes': 'Heeft foto',
          'no': 'Geen foto',
        },
        defaultValue: 'all',
        filterCallback: (row, filter) => {
          switch(filter) {
            case "yes": return row.employee_picture_url !== null
            case "no": return row.employee_picture_url === null
            default: return true
          }
        },
      },
      {
        title: 'Activiteit',
        property: 'last_activity',
        options: {
          'all': 'Alle',
          'last7days': 'Laatste 7 dagen',
          'last30days': 'Laatste 30 dagen',
          'longer30days': 'Langer dan 30 dagen',
          'never': 'Nooit ingelogd',
        },
        defaultValue: 'all',
        filterCallback: (row, filter) => {
          switch(filter) {
            case "last7days": return moment(row.last_activity).isAfter(moment().subtract(7, 'days'));
            case "last30days": return moment(row.last_activity).isAfter(moment().subtract(1, 'month'));
            case "longer30days": return moment(row.last_activity).isBefore(moment().subtract(1, 'month'));
            case "never": return row.last_activity === null
            default: return true
          }
        },
      },
    ]}
    columns={[
      {header: 'Naam', property: 'id', transform: (id, employee) => <div className={"flex items-center"}>
          { employee.sub ? <>
            <HoverHint key={id} hint={'Jane account'}>
              <FontAwesomeIcon icon={faCheckCircle} className={'text-emerald-500 text-lg'} />
            </HoverHint>
          </> : <>
            <HoverHint key={id} hint={'Niet in systeem'}>
              <FontAwesomeIcon icon={faWarning} className={'text-amber-500 text-lg'} />
            </HoverHint>
          </>}
          <div className={"flex-1 ml-3"}>
            <small className={'text-gray-500'}>{employee.employee_number} &middot; {employee.role}</small>
            <div>
              {employee.given_name} <strong>{employee.family_name}</strong>
            </div>
          </div>
        </div>},
      {header: 'Email', property: 'email'},
      {header: 'Team', property: 'team_name'},
      {header: 'Laatst actief op', property: 'last_activity', transform: (time, employee) => {
          if (time === null) {
            return <div className="flex items-center">Nooit ingelogd</div>;
          }
          const ts = moment(time);
          if (ts.isSame(moment(), 'day')) {
            return <div className="flex items-center">Vandaag</div>;
          }
          if (ts.isSame(moment().subtract(1, 'day'), 'day')) {
            return <div className="flex items-center">Gisteren</div>;
          }
          if (ts.isSame(moment(), 'week')) {
            return <div className="flex items-center">Deze week</div>;
          }
          return <div className="flex items-center">
              {ts.format('DD-MM-YYYY')}
            </div>
        }},
      {header: 'Acties', property: 'id', transform: (id, row) => {
          const teamIds = teamFilterOptions.map(([id]) => id)
          if (canLimitedListAllTeams && (row.team_id === null || !teamIds.includes(row.team_id.toString()))) {
            return <NavLink to={`/employees/${id}`} className={'text-brand-primary'}><HoverHint key={id} hint={'Deze medewerker zit in een team waar jij beperkte toegang tot hebt'}>
              <FontAwesomeIcon icon={faWarning} className={'text-brand-primary mr-1'} />
            </HoverHint> Beheer</NavLink>
          }
          if (!canListAllTeams && (row.team_id === null || !teamIds.includes(row.team_id.toString()))) {
            return <HoverHint key={id} hint={'Deze medewerker zit in een team waar jij geen toegang tot hebt'}>
              <FontAwesomeIcon icon={faTimesCircle} className={'text-gray-500 mr-1'} /> <span>Geen toegang</span>
            </HoverHint>
          }
          return <NavLink to={`/employees/${id}`} className={'text-brand-primary'}>Beheer</NavLink>
        }},
    ]}
    loading={false}
  />
}

